import { RetriggerOrganisationWebhookDeliveryDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

export interface RetriggerDeliveryResult {
  status: 'queued' | 'already_queued' | 'not_replayable';
}

const RETRIGGER_STATUSES = [
  'queued',
  'already_queued',
  'not_replayable',
] as const;

const isRetriggerStatus = (
  value: string
): value is RetriggerDeliveryResult['status'] =>
  RETRIGGER_STATUSES.some((status) => status === value);

// No cache invalidation (no `evictFields`/`invalidate`): the re-queued row only
// appears ~1 min later via the retry sweeper, so refetching now shows nothing
// new. Both branches converge on the normalised `{ status }` shape — the tRPC
// result is already typed, and the Apollo wire string is narrowed through
// `isRetriggerStatus` in `mapGraphqlResponse`.
export const useRetriggerDelivery = createMutationHook({
  name: 'retriggerDelivery',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.webhook.retriggerDelivery.mutationOptions({ onSuccess }),
  mapInput: (id: string) => ({ id }),
  mapResponse: (result: {
    status: RetriggerDeliveryResult['status'];
  }): RetriggerDeliveryResult => ({ status: result.status }),
  graphqlDocument: RetriggerOrganisationWebhookDeliveryDocument,
  graphqlVariables: (id) => ({ object: { Id: id } }),
  mapGraphqlResponse: (data): RetriggerDeliveryResult => {
    const status = data.retriggerOrganisationWebhookDeliveryApi?.status;
    if (status === undefined || !isRetriggerStatus(status)) {
      throw new Error('Failed to re-trigger delivery');
    }

    return { status };
  },
});
